"use client"

import type React from "react"
import { useState, useEffect } from "react"
import { AddClientProjectOffcanvas } from "./add"
import { DeleteClientProjectModal } from "./delete"
import { EditClientProjectOffcanvas } from "./edit"
import { ClientProjectsList } from "./list"
import api from "../../libs/axios"
import { useDebounce } from "../../hooks/useDebounce"

interface Client {
  id: number
  client_full_name: string
  client_short_name: string
  is_active: boolean
}

interface Project {
  id: number
  project_name: string
  status: string
  created_at: string
}

interface ClientProject {
  id: number
  client_id: number
  client_project_name: string
  client_project_description?: string
  is_active: boolean
  created_at: string
  updated_at: string
  created_by: number
  suspended_by?: number
  suspended_at?: string
  client?: Client
  projects?: Project[]
  project_count?: number
}

interface Pagination {
  offset: number
  limit: number
  page: number
  total_pages: number
  total_count: number
  has_next_page: boolean
  has_prev_page: boolean
  next_page: number | null
  prev_page: number | null
}

interface ClientProjectFormData {
  client_id: number
  client_project_name: string
  client_project_description: string
  status?: "ACTIVE" | "SUSPENDED"
}

interface EditClientProjectFormData extends ClientProjectFormData {
  id: number
}

export const ClientProjectsManagement: React.FC = () => {
  // State management
  const [clientProjects, setClientProjects] = useState<ClientProject[]>([])
  const [clients, setClients] = useState<Client[]>([])
  const [pagination, setPagination] = useState<Pagination>({
    offset: 0,
    limit: 10,
    page: 1,
    total_pages: 1,
    total_count: 0,
    has_next_page: false,
    has_prev_page: false,
    next_page: null,
    prev_page: null,
  })

  // Loading states
  const [clientProjectsLoading, setClientProjectsLoading] = useState(false)
  const [actionLoading, setActionLoading] = useState(false)

  // Modal/Offcanvas states
  const [showAddClientProject, setShowAddClientProject] = useState(false)
  const [showEditClientProject, setShowEditClientProject] = useState(false)
  const [showDeleteModal, setShowDeleteModal] = useState(false)
  const [selectedClientProject, setSelectedClientProject] = useState<ClientProject | null>(null)

  // Search and filter states
  const [searchTerm, setSearchTerm] = useState("")
  const debouncedSearch = useDebounce(searchTerm, 400)
  const [statusFilter, setStatusFilter] = useState<string>("")
  const [clientFilter, setClientFilter] = useState<string>("")
  const [sortConfig, setSortConfig] = useState<{ field: string; direction: 'ASC' | 'DESC' }>({
    field: 'created_at',
    direction: 'DESC'
  })

  // API Functions
  const fetchClients = async () => {
    try {
      const response = await api.get("/clients/active")
      if (response.data.success) {
        setClients(response.data.data.clients || [])
      }
    } catch (error) {
      console.error("Error fetching clients:", error)
    }
  }

  const fetchClientProjects = async (
    search = "",
    status = "",
    clientId = "",
    page = 1,
    limitValue?: number,
    sort = sortConfig
  ) => {
    setClientProjectsLoading(true)
    try {
      const effectiveLimit = limitValue ?? pagination.limit ?? 10

      const queryParams: any = {
        page: page,
        limit: effectiveLimit,
      }

      if (search.trim()) {
        queryParams.search = search
      }

      if (status.trim()) {
        queryParams.status = status
      }

      if (clientId.trim()) {
        queryParams.client_id = clientId
      }

      if (sort?.field) {
        queryParams.sort_by = sort.field
        queryParams.sort_order = sort.direction
      }

      const response = await api.get("/client-projects/list", {
        params: queryParams,
      })

      if (response.data.success) {
        setClientProjects(response.data.data.client_projects || [])
        setPagination(response.data.data.pagination || pagination)
      } else {
        console.error("API returned success: false")
      }
    } catch (error) {
      console.error("Error fetching client projects:", error)
    } finally {
      setClientProjectsLoading(false)
    }
  }

  const fetchClientProjectById = async (clientProjectId: number): Promise<ClientProject | null> => {
    try {
      const response = await api.get(`/client-projects/${clientProjectId}`)

      if (response.data.success) {
        return response.data.data.client_project
      }
      return null
    } catch (error) {
      console.error("Error fetching client project by ID:", error)
      return null
    }
  }

  const createClientProject = async (clientProjectData: ClientProjectFormData) => {
    setActionLoading(true)
    try {
      const response = await api.post("/client-projects/create", clientProjectData)

      if (response.data.success) {
        await fetchClientProjects()
        setShowAddClientProject(false)
        console.log("Client project created successfully")
      } else {
        console.error("Failed to create client project:", response.data.message)
      }
    } catch (error) {
      console.error("Error creating client project:", error)
    } finally {
      setActionLoading(false)
    }
  }

  const updateClientProject = async (clientProjectData: EditClientProjectFormData) => {
    setActionLoading(true)
    try {
      const response = await api.put(`/client-projects/${clientProjectData.id}`, clientProjectData)

      if (response.data.success) {
        await fetchClientProjects()
        setShowEditClientProject(false)
        setSelectedClientProject(null)
        console.log("Client project updated successfully")
      } else {
        console.error("Failed to update client project:", response.data.message)
      }
    } catch (error) {
      console.error("Error updating client project:", error)
    } finally {
      setActionLoading(false)
    }
  }

  const deleteClientProject = async () => {
    if (!selectedClientProject) return

    setActionLoading(true)
    try {
      const response = await api.delete(`/client-projects/${selectedClientProject.id}`)

      if (response.data.success) {
        await fetchClientProjects()
        setShowDeleteModal(false)
        setSelectedClientProject(null)
        console.log("Client project deleted successfully")
      } else {
        console.error("Failed to delete client project:", response.data.message)
      }
    } catch (error) {
      console.error("Error deleting client project:", error)
    } finally {
      setActionLoading(false)
    }
  }

  const toggleClientProjectStatus = async (clientProject: ClientProject, newStatus: "ACTIVE" | "SUSPENDED") => {
    setActionLoading(true)
    try {
      const response = await api.patch(`/client-projects/${clientProject.id}/status`, {
        status: newStatus,
      })

      if (response.data.success) {
        await fetchClientProjects()
        console.log(`Client project ${newStatus.toLowerCase()} successfully`)
      } else {
        console.error("Failed to update client project status:", response.data.message)
      }
    } catch (error) {
      console.error("Error updating client project status:", error)
    } finally {
      setActionLoading(false)
    }
  }

  // Event handlers
  const handleSearch = (search: string) => {
    setSearchTerm(search)
    // API call triggered by debouncedSearch effect below
  }

  // Fire fetch when debounced search value settles
  useEffect(() => {
    fetchClientProjects(debouncedSearch, statusFilter, clientFilter, 1)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [debouncedSearch])

  const handleStatusFilter = (status: string) => {
    setStatusFilter(status)
    fetchClientProjects(searchTerm, status, clientFilter, 1, undefined, sortConfig)
  }

  const handleClientFilter = (clientId: string) => {
    setClientFilter(clientId)
    fetchClientProjects(searchTerm, statusFilter, clientId, 1, undefined, sortConfig)
  }

  const handlePageChange = (page: number) => {
    fetchClientProjects(searchTerm, statusFilter, clientFilter, page, undefined, sortConfig)
  }

  const handleLimitChange = (limit: number) => {
    setPagination((prev) => ({ ...prev, limit, page: 1 }))
    fetchClientProjects(searchTerm, statusFilter, clientFilter, 1, limit, sortConfig)
  }

  const handleAddClientProject = () => {
    setShowAddClientProject(true)
  }

  const handleEditClientProject = async (clientProject: ClientProject) => {
    const fullClientProject = await fetchClientProjectById(clientProject.id)
    if (fullClientProject) {
      setSelectedClientProject(fullClientProject)
      setShowEditClientProject(true)
    }
  }

  const handleDeleteClientProject = (clientProject: ClientProject) => {
    setSelectedClientProject(clientProject)
    setShowDeleteModal(true)
  }

  const handleToggleStatus = (clientProject: ClientProject) => {
    const newStatus = clientProject.is_active ? "SUSPENDED" : "ACTIVE"
    toggleClientProjectStatus(clientProject, newStatus)
  }

  const handleCloseModals = () => {
    setShowAddClientProject(false)
    setShowEditClientProject(false)
    setShowDeleteModal(false)
    setSelectedClientProject(null)
  }

  // Initialize data on component mount
  useEffect(() => {
    fetchClients()
    fetchClientProjects()
  }, [])

  const handleSort = (field: string, direction: 'ASC' | 'DESC') => {
    const sort = { field, direction }
    setSortConfig(sort)
    fetchClientProjects(searchTerm, statusFilter, clientFilter, pagination.page, undefined, sort)
  }

  return (
    <div className="">
      <div className="">
        {/* Main Client Projects List */}
        <ClientProjectsList
          clientProjects={clientProjects}
          clients={clients}
          pagination={pagination}
          onSearch={handleSearch}
          onStatusFilter={handleStatusFilter}
          onClientFilter={handleClientFilter}
          onPageChange={handlePageChange}
          onLimitChange={handleLimitChange}
          onAddClientProject={handleAddClientProject}
          onEditClientProject={handleEditClientProject}
          onDeleteClientProject={handleDeleteClientProject}
          onToggleStatus={handleToggleStatus}
          onSort={handleSort}
          loading={clientProjectsLoading}
          searchValue={searchTerm}
          statusValue={statusFilter}
          clientValue={clientFilter}
          sortState={sortConfig}
        />

        {/* Add Client Project Offcanvas */}
        <AddClientProjectOffcanvas
          isOpen={showAddClientProject}
          onClose={handleCloseModals}
          onSubmit={createClientProject}
          clients={clients}
          loading={actionLoading}
        />

        {/* Edit Client Project Offcanvas */}
        <EditClientProjectOffcanvas
          isOpen={showEditClientProject}
          onClose={handleCloseModals}
          onSubmit={updateClientProject}
          clientProject={selectedClientProject}
          clients={clients}
          loading={actionLoading}
        />

        {/* Delete Client Project Modal */}
        <DeleteClientProjectModal
          isOpen={showDeleteModal}
          onClose={handleCloseModals}
          onDelete={deleteClientProject}
          text={
            selectedClientProject
              ? `Are you sure you want to delete the client project "${selectedClientProject.client_project_name}"? This will permanently remove the client project and may affect associated projects.`
              : ""
          }
          loading={actionLoading}
        />
      </div>
    </div>
  )
}
